import { forCampaign, holdTitle, type Hold } from './holds';

/**
 * Что с кампанией на самом деле.
 *
 * ПЕРВАЯ РЕДАКЦИЯ ЭТОГО ЭКРАНА ВРАЛА, и врала уверенно: «ничего
 * не удерживает — кампания работает» стояло под четырьмястами
 * кампаниями, половина которых завершена год назад, а половина на паузе.
 * Ошибка была в подмене понятий: удержание — это НАШ предохранитель,
 * а не состояние кампании. Кампания, поставленная на паузу в самом
 * Wildberries, никаких удержаний не имеет и при этом не работает.
 *
 * Правда собирается из трёх источников, и порядок важен:
 *   1) наше удержание — если стоим мы, надо сказать ПОЧЕМУ;
 *   2) статус у Wildberries — пауза, завершена, готова, отказ;
 *   3) расход — «активна» с нулём за неделю фактически стоит.
 *
 * Третий пункт взят из трекера, где он записан прямо: «активная
 * кампания, которая ничего не потратила, фактически стоит — это важнее
 * статуса». Без него экран говорит «работает» про кампанию, которая
 * не показывается никому.
 */

/** Коды состояний Wildberries. Иные значения не прячем — показываем как есть. */
export const WB_STATUS: Record<number, string> = {
  [-1]: 'удаляется',
  4: 'готова, но не запущена',
  7: 'завершена',
  8: 'отказ',
  9: 'активна',
  11: 'на паузе',
};

export type Health = 'working' | 'idle' | 'paused' | 'held' | 'done';

export interface State {
  health: Health;
  /**
   * ═════════════════════════════════════════════════════════════════
   * ЯРЛЫК В ДВЕ-ТРИ БУКВЫ, И ОН СО СТРОЧНОЙ: «работает», «не тратит»,
   * «на паузе».
   *
   * ТАК В ЭТАЛОНЕ, И ТАК ПРАВИЛЬНО. Плашка состояния — не предложение
   * и не заголовок: она стоит В СТРОКЕ, сразу за именем кампании
   * и её номером, и прописная буква в ней читается как начало новой
   * мысли. Заодно совпадает с тем, как Wildberries называет
   * состояния у себя (`WB_STATUS` выше — тоже строчными).
   *
   * КОРОТКИЙ, А НЕ ЗАКОНЧЕННАЯ ФРАЗА. Первая редакция писала под каждой
   * кампанией целое предложение — «Числится активной, но за окно
   * не потратила ни рубля: проверьте остаток и ставки». В списке из сорока
   * строк это сорок предложений: читать их подряд невозможно, а не читая,
   * не отличить одну строку от другой. Ярлык виден боковым зрением,
   * объяснение — по наведению.
   */
  text: string;
  /** Полное объяснение — во всплывающей подсказке. `null`, если ярлыка хватает. */
  hint: string | null;
  /** Показывать ли кампанию в списке по умолчанию. */
  live: boolean;
}

/**
 * Почему активная кампания не тратит.
 *
 * ЧЕГО НЕ ЗНАЕМ — О ТОМ НЕ ГАДАЕМ. Остатки товара мы не выгружаем вовсе,
 * и «кончился товар» было бы догадкой, выданной за причину. Поэтому
 * последняя ветка честно говорит, что причина не в наших данных,
 * и называет две самые частые — не утверждая ни одну.
 */
function idleReason(c: { noProducts?: boolean; noBids?: boolean }): {
  text: string;
  hint: string;
} {
  if (c.noProducts) {
    return {
      text: 'нет товаров',
      hint: 'Числится активной, но в ней нет ни одного товара — показывать нечего',
    };
  }
  if (c.noBids) {
    return {
      text: 'ставки не заданы',
      hint: 'Числится активной, но ставки не заданы — показов не будет',
    };
  }
  return {
    text: 'не тратит',
    hint:
      'Числится активной, но за последние семь суток не потратила ни рубля. Чаще всего ' +
      'кончился остаток на счёте кампании — его мы не выгружаем, проверьте его и ставки',
  };
}

/** Кампании, которые уже ничего не сделают: завершённые, отказ, удаляемые. */
export function isArchived(wbStatus: number): boolean {
  return wbStatus === 7 || wbStatus === 8 || wbStatus === -1;
}

/**
 * Идёт ли кампания сейчас — по ДЕЙСТВИТЕЛЬНОМУ состоянию, а не по статусу
 * Wildberries.
 *
 * ОТСЮДА БЫЛА ОШИБКА, И ОНА СТОИЛА ВЛАДЕЛЬЦУ НАЖАТИЯ ВПУСТУЮ. Кнопка
 * пуск-пауза смотрела на `wbStatus`. При холостом ходе команда в WB
 * не уходит, статус там остаётся «активна», а наше удержание уже стоит:
 * кампания на экране «остановлена вручную» — и кнопка предлагает
 * остановить её ещё раз. Возобновить было нечем.
 *
 * То же самое случилось бы и на бою: между нашей паузой и обновлением
 * статуса из Wildberries проходит до пяти минут, и всё это время кнопка
 * показывала бы не то.
 */
export function isRunning(state: State): boolean {
  return state.health === 'working' || state.health === 'idle';
}

export function stateOf(
  campaign: {
    id: string;
    wbStatus: number;
    /**
     * Расход за ПОСТОЯННОЕ окно, а не за выбранный период.
     *
     * СОСТОЯНИЕ — СВОЙСТВО «СЕЙЧАС», А НЕ СВОЙСТВО ОТЧЁТА. Пока здесь
     * стоял периодный расход, одна и та же кампания была «Работает»
     * в карточке (тридцать суток) и «Не тратит» в списке (выбранные
     * семь) — поймано владельцем 14.08.2026 на живом экране. А выбери
     * он «сегодня», к утру половина кабинета числилась бы простаивающей
     * и горела бы оранжевым.
     */
    spendRecent: number;
    noProducts?: boolean;
    noBids?: boolean;
  },
  holds: Hold[],
): State {
  // 1. Стоим мы — говорим почему. Наша причина точнее статуса: WB покажет
  //    просто «на паузе», а человеку нужно знать, что это дневной лимит.
  const mine = forCampaign(holds, campaign.id);
  if (mine.length > 0) {
    const more = mine.length - 1;
    return {
      health: 'held',
      live: true,
      text:
        `стоит: ${holdTitle(mine[0]!.kind).toLowerCase()}` +
        (more > 0 ? ` и ещё ${more}` : ''),
      hint: mine.map((h) => h.reason).join('; '),
    };
  }

  // 2. Состояние у Wildberries.
  if (isArchived(campaign.wbStatus)) {
    return {
      health: 'done',
      live: false,
      text: WB_STATUS[campaign.wbStatus] ?? `статус ${campaign.wbStatus}`,
      hint: 'Кампанию Wildberries нельзя вернуть в работу',
    };
  }
  if (campaign.wbStatus === 11) {
    return { health: 'paused', live: true, text: 'на паузе', hint: null };
  }
  if (campaign.wbStatus === 4) {
    return { health: 'paused', live: true, text: 'готова, не запущена', hint: null };
  }

  // 3. Числится активной — но проверяем расход.
  if (campaign.wbStatus === 9) {
    if (campaign.spendRecent > 0)
      return { health: 'working', live: true, text: 'работает', hint: null };

    // Не «работает» и не «стоит»: числится активной, а денег не тратит.
    // ПРИЧИНУ НАЗЫВАЕМ, КОГДА ЗНАЕМ. «Не тратит» без объяснения человек
    // читает как поломку сервиса и идёт искать её у нас — а причина чаще
    // всего простая и видна из наших же данных.
    return { health: 'idle', live: true, ...idleReason(campaign) };
  }

  return {
    health: 'paused',
    live: true,
    text: WB_STATUS[campaign.wbStatus] ?? `статус ${campaign.wbStatus}`,
    hint: null,
  };
}

/**
 * Цвет состояния — ДВЕ ЛАМПЫ, и это осознанное упрощение.
 *
 * ЗЕЛЁНЫЙ — КАМПАНИЯ ПОКАЗЫВАЕТСЯ. ОРАНЖЕВЫЙ — НЕ ПОКАЗЫВАЕТСЯ, по любой
 * причине: пауза в Wildberries, наше удержание, «числится активной
 * и не тратит».
 *
 * ЗДЕСЬ БЫЛО ЧЕТЫРЕ ЦВЕТА: зелёный, оранжевый «не тратит», красный
 * «держим мы», серый «на паузе». Каждый был по-своему осмыслен, и вместе
 * они не работали. Владелец, глядя на живой список, читал его вслух
 * так: «полоска красная, а кампания работает», «на паузе, а горит
 * зелёным». Четыре цвета требуют держать в голове словарь; светофор
 * не требует ничего, и потому его понимают все.
 *
 * ПРИЧИНА ОСТАНОВКИ НИКУДА НЕ ДЕЛАСЬ — она в словах рядом: «Стоит:
 * расписание», «Стоит: остановлено вручную», «Не тратит», «На паузе».
 * Слово различает четыре случая точнее, чем оттенок, и не требует
 * их запоминать.
 *
 * ЭТИМ ЖЕ ЦВЕТОМ КРАСИТСЯ КНОПКА ПОКАЗОВ. Требование владельца дословно:
 * «если кнопка оранжевым, значит и полоска тоже оранжевым». Один
 * источник цвета — единственный способ этого добиться; два правила,
 * даже похожих, однажды разойдутся, и мы это уже проходили сегодня.
 *
 * СЕРЫЙ ОСТАЛСЯ ОДИН — для завершённых: у них нет состояния, которое
 * можно изменить, и светофор к ним не относится вовсе.
 */
export type Tone = 'run' | 'idle' | 'dead';

/**
 * Какая лампа горит.
 *
 * ВОЗВРАЩАЕТСЯ НЕ ЦВЕТ, А ИМЯ СОСТОЯНИЯ. Цвет лежит в `styles.css`
 * (`rk-light-*`, `rk-stripe-*`) и взят из образца оформления; здесь
 * решается только то, какое состояние показывать. Прежде отсюда уходила
 * строка `var(--good)`, и один и тот же оттенок приходилось вписывать
 * и в кнопку, и в полосу, и в рамку — три места на одно решение.
 *
 * ЛАМП ТРИ, А НЕ ЧЕТЫРЕ. В образце есть и четвёртая, `held` — рыжая,
 * «держим мы». Она не зажигается по решению владельца: причина остановки
 * стоит словами рядом («Стоит: расписание», «На паузе»), а слово
 * различает четыре случая точнее, чем оттенок, и не требует их
 * запоминать.
 */
export function toneOf(h: Health): Tone {
  if (h === 'done') return 'dead';
  return h === 'working' ? 'run' : 'idle';
}

/**
 * Сводка по кабинету одной строкой.
 *
 * СЧИТАЕТСЯ ПО ЖИВЫМ, а не по всем: «463 кампании» при четырёхстах
 * завершённых — число, которое ничего не говорит и мешает увидеть
 * те двадцать, что работают сегодня.
 */
export function overview(states: State[]): string {
  if (states.length === 0) return 'Кампаний нет';

  /* ЗАВЕРШЁННЫХ ЗДЕСЬ НЕТ ВОВСЕ — и с 28.08.2026 это ПРАВДА, а не
     договорённость. Строка стояла здесь и раньше, но сервер завершённые
     отдавал, и `states` ими был полон: `by('working')` считалось верно,
     а «Всего 464» врало в глаза. Теперь `campaigns()` архив отбрасывает
     (`ARCHIVED_STATUSES` в `src/rk/campaign-state.ts`), и «всего»
     наконец означает то, что написано.

     ГДЕ АРХИВ ВСЁ-ТАКИ ВИДЕН — на плитках наверху: «завершено 428»
     и «завершённые 380 100 ₽». Их считает другой запрос, без отбора
     по статусу. */
  const by = (h: Health) => states.filter((s) => s.health === h).length;
  const parts = [`работают ${by('working')}`];
  if (by('idle') > 0) parts.push(`не тратят ${by('idle')}`);
  if (by('held') > 0) parts.push(`стоят по нашим правилам ${by('held')}`);
  if (by('paused') > 0) parts.push(`на паузе ${by('paused')}`);

  return `Всего ${states.length}: ${parts.join(', ')}`;
}

/** Порядок: сперва то, что требует внимания, потом работающее. */
export function rank(s: State): number {
  switch (s.health) {
    case 'held':
      return 0;
    case 'idle':
      return 1;
    case 'working':
      return 2;
    case 'paused':
      return 3;
    default:
      return 4;
  }
}
